// Rename a recording (FR-L2). One dialog for every place that offers it: the library card's
// menu, the watch page's editor row and the meeting notes header. It only ever sends `title`;
// the server trims it and holds the 1-200 char rule (grackles.rs `validate_title`), and its
// edit check (`authz::authorize_edit`) is the last word on who may rename.
import { useState, type FormEvent } from "react";
import { Dialog } from "@grackle/ui";
import { ApiFail } from "../api/client";
import { Button, Field, Notice } from "../components/ui";
import { renameGrackle } from "./api";

export const TITLE_MAX = 200;

export default function RenameGrackleDialog({
  grackleId,
  title,
  onClose,
  onRenamed,
}: {
  grackleId: string;
  title: string;
  onClose: () => void;
  /** Called with the title the server stored (trimmed), after the dialog's work is done. */
  onRenamed: (title: string) => void;
}) {
  const [value, setValue] = useState(title);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed) return;
    if (trimmed === title) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await renameGrackle(grackleId, trimmed);
      onRenamed(updated.title);
      onClose();
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't rename this recording.");
      setSaving(false);
    }
  }

  return (
    <Dialog open title="Rename recording" onClose={onClose} width={440}>
      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, padding: "4px 24px 24px" }}>
        <Field
          label="Title"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onFocus={(e) => e.currentTarget.select()}
          autoFocus
          maxLength={TITLE_MAX}
          required
        />
        {error ? <Notice tone="error">{error}</Notice> : null}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={saving} disabled={!value.trim()}>
            Rename
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
